import ModalComponent from '@components/modal/Modal.component';
import {InvoiceDetails, invoicesService} from '@store/invoices/invoices';
import NewTableComponent from '@components/table/NewTable.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {ExecutionLine} from '@store/invoices/executionLines';
import {GridRowParams, GridValueFormatterParams, GridValueGetterParams} from '@mui/x-data-grid-pro';
import {NumberExt} from '@utils/extensions/number.extension';
import {Box, Button, Stack, Typography} from '@mui/material';
import {CheckCircleGreen, CheckCircleGrey} from '@icons/index';
import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import {Colors} from '@constants/colors.constants';
import ConfirmModal from '@components/modal/Confirm.modal';
import {useSnackbar} from 'notistack';
import {useNavigate} from 'react-router-dom';

interface CancelBillModalProps {
  billDetails?: InvoiceDetails;
  handleClose: () => void;
}

const CancelBillModal = (props: CancelBillModalProps) => {
  const {handleClose, billDetails} = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const [reason, setReason] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const [selectedInvoiceLines, setSelectedInvoiceLines] = useState<number[]>([]);
  const searchedExecutionLines = useMemo(() => billDetails?.clientInvoiceLines ?? [], [billDetails?.clientInvoiceLines]);

  const selectedLines = searchedExecutionLines.filter((line) => selectedInvoiceLines.includes(line.id));

  const handleCancelBill = () => {
    if (billDetails?.id) {
      invoicesService.cancelInvoice(billDetails?.id, selectedInvoiceLines, reason).subscribe({
        next: (value) => {
          handleClose();
          navigate(`/invoices/bills/${value.id}`);
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  return (<>
    <ModalComponent
      handleClose={handleClose}
      maxWidth={false}
      title={t('invoices.bills.cancelModal.title')}
      content={(
        <Stack pt="30px" pb="25px" height="85vh" width="100%" spacing={2}>
          <NewTableComponent
            columns={[
              COLUMN_TYPES.DATE({
                field: 'execution.executedAt',
                cellClassName: () => 'secondary',
                hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                label: t('invoices.executions.table.executedAt'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.executedAt,
                valueFormatter: (params: GridValueFormatterParams) =>
                  params.value ? t('global.date', {date: new Date(params.value)}) : '',
                width: 150,
              }),
              COLUMN_TYPES.TEXT({
                field: 'execution.clientOrganisationUnitName',
                label: t('invoices.executions.clientOrganisationUnitName'),
                valueGetter: (params: GridValueGetterParams<ExecutionLine, ExecutionLine>) => params.row.clientOrganisationUnitName,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'name',
                color: 'secondary',
                label: t('invoices.executions.table.name'),
                fullWidth: true,
                minWidth: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'serviceKind.kind',
                cellClassName: () => 'bold',
                hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                label: t('invoices.executions.table.type'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.kind,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'additionalInformation',
                color: 'secondary',
                label: t('invoices.executions.table.additionalInformation'),
                minWidth: 100,
              }),
              COLUMN_TYPES.TEXT({
                field: 'waste',
                color: 'secondary',
                label: t('invoices.executions.table.waste'),
                // valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.waste,
                width: 250,
              }),
              COLUMN_TYPES.TEXT({
                field: 'container',
                color: 'secondary',
                label: t('invoices.executions.table.container'),
                // valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.container,
                width: 200,
              }),
              COLUMN_TYPES.TEXT({
                field: 'executionLine.quantity',
                label: t('invoices.executions.table.quantity'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.quantity,
                width: 100,
              }),
              COLUMN_TYPES.TEXT({
                field: 'serviceKind.execUnitName1',
                color: 'secondary',
                label: t('invoices.executions.table.unit'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.unit,
                width: 100,
              }),
              COLUMN_TYPES.TEXT({
                field: 'executionLine.puSell',
                label: t('invoices.executions.table.puSell'),
                valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                  params.row.puSell ? NumberExt.round(params.row.puSell / 100, 2) : undefined,
                width: 100
              }),
              COLUMN_TYPES.TEXT({
                field: 'totalSell',
                label: t('invoices.executions.table.totalSell'),
                valueFormatter: (params: GridValueFormatterParams) =>
                  params.value ? NumberExt.round(params.value / 100, 2) : '',
                width: 100,
              }),
              COLUMN_TYPES.ICON({
                field: 'supplierInvoiceLineId',
                label: t('invoices.executions.table.purchaseStatementShort'),
                renderIcon: (executionLine: ExecutionLine) => {
                  if (!executionLine.supplierInvoicingRequired) return <Box>-</Box>;
                  return executionLine.supplierInvoiceLineId ? <CheckCircleGreen/> : <CheckCircleGrey/>;
                },
                width: 40,
              }),
            ]}
            paginationMode="client"
            disablePagination
            sortingMode="client"
            autoHeight={false}
            sortModel={[
              {field: 'execution.executedAt', sort: 'asc'},
              {field: 'waste', sort: 'asc'},
              {
                field: 'container',
                sort: 'asc',
              },
            ]}
            isRowSelectable={(params: GridRowParams<ExecutionLine>) => params.row.cancelledByLineId === null}
            handleRowSelected={setSelectedInvoiceLines}
            rowsSelected={selectedInvoiceLines}
            data={searchedExecutionLines}/>
          <Stack spacing={2} direction="column" bgcolor={Colors.white} p={2}>
            <Stack direction="row" spacing={2}>
              <Stack spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.cancelModal.originRef')}
                </Typography>
                <TextFieldComponent
                  inputWidth={250}
                  readOnly
                  value={billDetails?.invoiceRef}/>
              </Stack>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.cancelModal.reason')}
                </Typography>
                <TextFieldComponent
                  handleChange={(value) => setReason(value)}
                  value={reason}/>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Stack spacing={0.5}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.bills.details.totalAmountNoVatInCents')}
                  </Typography>
                  <NumberTextFieldComponent
                    readOnly
                    inputWidth={250}
                    endAdornment={
                      <Typography fontSize={14} fontWeight="400">
                        {billDetails?.currency}
                      </Typography>
                    }
                    value={NumberExt.round((billDetails?.totalAmountNoVatInCents ?? 0) / 100, 2)}
                  />
                </Stack>
                <Stack spacing={0.5}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.bills.cancelModal.cancelAmountVatIncluded')}
                  </Typography>
                  <NumberTextFieldComponent
                    readOnly
                    inputWidth={250}
                    endAdornment={
                      <Typography fontSize={14} fontWeight="400">
                        {billDetails?.currency}
                      </Typography>
                    }
                    value={NumberExt.round((selectedLines.reduce((acc, val) => acc + val.totalSell, 0) ?? 0) / 100, 2)}
                  />
                </Stack>
              </Stack>
            </Stack>

          </Stack>
        </Stack>
      )}
      actions={(
        <>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {t('global.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedInvoiceLines?.length}
            onClick={() => setShowConfirmModal(true)}
          >
            {t('global.validate')}
          </Button>
        </>
      )}/>;
    {showConfirmModal && (<ConfirmModal title={t('invoices.bills.cancelModal.confirmModal.title')}
                                        description={t('invoices.bills.cancelModal.confirmModal.body')}
                                        handleClose={() => setShowConfirmModal(false)}
                                        handleValidate={() => handleCancelBill()}
                                        validateButton={t('invoices.bills.cancelModal.confirmModal.button')}/>)}
  </>);
};

export default CancelBillModal;
